import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Truck, ExternalLink, Package, Shield } from "lucide-react";

const SHIPROCKET_URL = "https://www.shiprocket.in/shipment-tracking/";

export default function TrackOrder() {
  const [awb, setAwb] = useState("");

  const openTracking = (e) => {
    e?.preventDefault?.();
    const url = awb.trim()
      ? `${SHIPROCKET_URL}${encodeURIComponent(awb.trim())}`
      : SHIPROCKET_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="track-order-page">
      {/* Hero */}
      <div className="mb-12">
        <p className="label-small text-[#C5A059] mb-3">Sumanya · Shipping</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2C2A29]" data-testid="page-title">
          Track your order.
        </h1>
        <p className="text-lg text-[#595552] mt-4 max-w-2xl leading-relaxed">
          Every Sumanya order ships fully insured through <span className="font-semibold text-[#2C2A29]">Shiprocket</span>.
          Enter your AWB / tracking number below and we&apos;ll take you straight to the live shipment status.
        </p>
        <div className="mt-6 h-px w-24 bg-[#C5A059]" />
      </div>

      {/* Track form */}
      <form onSubmit={openTracking} className="bg-white border border-[#EAE3D9] rounded-2xl p-6 sm:p-8 mb-10" data-testid="track-form">
        <label className="label-small text-[#8A8480] mb-2 block">AWB / Tracking number</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            value={awb}
            onChange={(e) => setAwb(e.target.value)}
            placeholder="e.g. SR1234567890"
            className="h-12 text-base"
            data-testid="track-awb-input"
          />
          <Button
            type="submit"
            className="h-12 px-6 bg-[#8B1E28] hover:bg-[#7A1A24] text-white rounded-xl"
            data-testid="track-submit-btn"
          >
            <Truck className="h-4 w-4 mr-2" />
            Track on Shiprocket
            <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-80" />
          </Button>
        </div>
        <p className="text-xs text-[#8A8480] mt-3">
          You&apos;ll receive your AWB by SMS / email once your order is dispatched. If you don&apos;t have it handy, we can look it up for you — just{" "}
          <a href="/contact" className="text-[#8B1E28] hover:underline" data-testid="track-contact-link">contact us</a>.
        </p>
      </form>

      {/* Info cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-14">
        {[
          { Icon: Package, t: "Secure packaging", s: "Every piece packed in tamper-evident boxes with insurance." },
          { Icon: Truck, t: "Insured delivery", s: "Pan-India shipping through Shiprocket with signature-required delivery." },
          { Icon: Shield, t: "Damage protection", s: "If your parcel arrives damaged, tell us within 24 hours for a full replacement." },
        ].map((f, i) => (
          <div key={i} className="bg-[#FDFBF7] border border-[#EAE3D9] rounded-2xl p-5" data-testid={`track-info-${i}`}>
            <div className="p-2 inline-flex rounded-xl bg-white border border-[#EAE3D9] mb-3">
              <f.Icon className="h-5 w-5 text-[#8B1E28]" strokeWidth={1.6} />
            </div>
            <div className="font-semibold text-[#2C2A29] text-[15px] mb-1">{f.t}</div>
            <div className="text-[13px] text-[#595552] leading-relaxed">{f.s}</div>
          </div>
        ))}
      </div>

      {/* Help */}
      <div className="text-center">
        <div className="font-display text-2xl text-[#2C2A29] mb-2">Need help with tracking?</div>
        <p className="text-sm text-[#8A8480]">
          Phone: <a href="tel:+918210981248" className="text-[#8B1E28] hover:underline">+91-8210981248</a>
          {" · "}
          Email: <a href="mailto:info@sumanya.co" className="text-[#8B1E28] hover:underline">info@sumanya.co</a>
        </p>
      </div>
    </div>
  );
}
